import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatRupees, formatDate, todayISO } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import AttachmentsField from "@/components/AttachmentsField";
import {
  Plus, ShieldCheck, FileText, Receipt, Layers,
  Pencil, Trash2, ExternalLink, RefreshCw, Search, AlertTriangle,
  Calendar as CalIcon, Wallet, BadgeIndianRupee,
} from "lucide-react";

const KIND_META = {
  insurance:    { label: "Insurance",    icon: ShieldCheck, color: "amber",   accent: "bg-amber-50 text-amber-700 border-amber-200" },
  policy:       { label: "Policy",       icon: FileText,    color: "blue",    accent: "bg-blue-50 text-blue-700 border-blue-200" },
  license:      { label: "License",      icon: Receipt,     color: "emerald", accent: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  registration: { label: "Registration", icon: Layers,      color: "indigo",  accent: "bg-indigo-50 text-indigo-700 border-indigo-200" },
  other:        { label: "Other",        icon: Layers,      color: "slate",   accent: "bg-slate-50 text-slate-700 border-slate-200" },
};

const LIGHT = {
  green:   { cls: "bg-green-50 text-green-700 border-green-200", label: "OK" },
  yellow:  { cls: "bg-yellow-50 text-yellow-800 border-yellow-200", label: "SOON" },
  red:     { cls: "bg-red-50 text-red-700 border-red-200", label: "DUE" },
  expired: { cls: "bg-red-100 text-red-800 border-red-300", label: "EXPIRED" },
  grey:    { cls: "bg-slate-50 text-slate-600 border-slate-200", label: "—" },
};

const FREQ = [
  { v: "one_time",    l: "One-time" },
  { v: "monthly",     l: "Monthly" },
  { v: "quarterly",   l: "Quarterly" },
  { v: "half_yearly", l: "Half-yearly" },
  { v: "yearly",      l: "Yearly" },
];

const emptyForm = () => ({
  title: "", type_id: "", number: "", issued_by: "", holder_name: "",
  partner_id: "", investment_id: "",
  amount_paid: "", payment_date: todayISO(), sum_assured: "",
  premium_frequency: "yearly",
  start_date: todayISO(), expiry_date: "",
  maturity_date: "", next_premium_date: "",
  notes: "", document_url: "", payment_link: "",
});

export default function Compliance() {
  const { user } = useAuth();
  const canEdit = user?.role === "admin" || user?.role === "accounts";
  const [tab, setTab] = useState("all");
  const [items, setItems] = useState([]);
  const [types, setTypes] = useState([]);
  const [partners, setPartners] = useState([]);
  const [investments, setInvestments] = useState([]);
  const [q, setQ] = useState("");
  const [dlg, setDlg] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [detail, setDetail] = useState(null); // shows policy detail drawer + premium list
  const [renewDlg, setRenewDlg] = useState(null);
  const [premDlg, setPremDlg] = useState(null);
  const [typeDlg, setTypeDlg] = useState(false);
  const [typeForm, setTypeForm] = useState({ name: "", kind: "policy", color: "" });

  const load = async () => {
    const [pols, tps, prts, invs] = await Promise.all([
      api.get("/policies"),
      api.get("/policy-types"),
      api.get("/partners"),
      api.get("/investments"),
    ]);
    setItems(pols.data); setTypes(tps.data);
    setPartners(prts.data); setInvestments(invs.data);
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    let list = items;
    if (tab !== "all") list = list.filter((p) => p.kind === tab);
    if (q.trim()) {
      const s = q.trim().toLowerCase();
      list = list.filter((p) =>
        (p.title || "").toLowerCase().includes(s) ||
        (p.number || "").toLowerCase().includes(s) ||
        (p.holder_name || "").toLowerCase().includes(s) ||
        (p.issued_by || "").toLowerCase().includes(s)
      );
    }
    return list;
  }, [items, tab, q]);

  const kpis = useMemo(() => {
    const active = items.filter((p) => p.status === "active" || p.status === "expired");
    const totalPaid = items.reduce((a, p) => a + Number(p.amount_paid || 0), 0);
    const totalSum = active.reduce((a, p) => a + Number(p.sum_assured || 0), 0);
    const urgent = items.filter((p) =>
      (p.status === "active" || p.status === "expired") &&
      p.days_to_expiry !== null && p.days_to_expiry !== undefined && p.days_to_expiry <= 15
    );
    return { total: items.length, active: active.length, totalPaid, totalSum, urgent: urgent.length };
  }, [items]);

  const openNew = () => { setEditing(null); setForm(emptyForm()); setDlg(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({
      title: p.title || "", type_id: p.type_id || "", number: p.number || "",
      issued_by: p.issued_by || "", holder_name: p.holder_name || "",
      partner_id: p.partner_id || "", investment_id: p.investment_id || "",
      amount_paid: p.amount_paid ?? "", payment_date: p.payment_date || "",
      sum_assured: p.sum_assured ?? "", premium_frequency: p.premium_frequency || "yearly",
      start_date: p.start_date || todayISO(), expiry_date: p.expiry_date || "",
      maturity_date: p.maturity_date || "", next_premium_date: p.next_premium_date || "",
      notes: p.notes || "", document_url: p.document_url || "", payment_link: p.payment_link || "",
    });
    setDlg(true);
  };

  const savePolicy = async () => {
    if (!form.title) return toast.error("Title is required");
    if (!form.start_date || !form.expiry_date) return toast.error("Start & expiry dates are required");
    const payload = {
      ...form,
      type_id: form.type_id || null,
      partner_id: form.partner_id || null,
      investment_id: form.investment_id || null,
      amount_paid: Number(form.amount_paid || 0),
      sum_assured: Number(form.sum_assured || 0),
      payment_date: form.payment_date || null,
      maturity_date: form.maturity_date || null,
      next_premium_date: form.next_premium_date || null,
      payment_link: form.payment_link || "",
    };
    try {
      if (editing) {
        await api.put(`/policies/${editing.id}`, payload);
        toast.success("Policy updated");
      } else {
        await api.post("/policies", payload);
        toast.success("Policy added");
      }
      setDlg(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const del = async (p) => {
    if (!window.confirm(`Delete ${p.title}? This cannot be undone.`)) return;
    try { await api.delete(`/policies/${p.id}`); load(); toast.success("Deleted"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const saveType = async () => {
    if (!typeForm.name) return toast.error("Name is required");
    try {
      await api.post("/policy-types", typeForm);
      toast.success("Type added");
      setTypeDlg(false); setTypeForm({ name: "", kind: "policy", color: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="eyebrow">Compliance</div>
          <h1 className="mt-2 font-editorial font-light text-[38px] leading-[1.02] sm:text-5xl md:text-6xl text-slate-900 tracking-[-0.035em]" data-testid="compliance-title">
            Policies, licenses<br/>& <em className="italic">insurances.</em>
          </h1>
          <p className="mt-2 text-sm text-slate-500 max-w-lg">
            Every dated document in one place — with automatic 15-day expiry & premium alerts.
          </p>
        </div>
        {canEdit && (
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => setTypeDlg(true)} className="rounded-md" data-testid="add-type-btn">
              <Plus className="h-4 w-4 mr-1.5" /> New type
            </Button>
            <Button onClick={openNew} className="rounded-md bg-red-600 hover:bg-red-700 text-white" data-testid="new-policy-btn">
              <Plus className="h-4 w-4 mr-1.5" /> New policy
            </Button>
          </div>
        )}
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4">
        <Metric label="Total records" value={kpis.total} icon={FileText} testId="kpi-total" />
        <Metric label="Active" value={kpis.active} icon={ShieldCheck} testId="kpi-active" />
        <Metric label="Total paid" value={formatRupees(kpis.totalPaid)} icon={Wallet} testId="kpi-paid" />
        <Metric label="Sum assured" value={formatRupees(kpis.totalSum)} icon={BadgeIndianRupee} testId="kpi-sum" />
        <Metric label="Due <15 days" value={kpis.urgent} icon={AlertTriangle} tone={kpis.urgent > 0 ? "text-red-600" : "text-slate-900"} testId="kpi-urgent" />
      </div>

      {/* Tab bar + search */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="inline-flex items-center gap-1 border border-slate-200 bg-white rounded-md p-1">
          <TabBtn v="all" tab={tab} setTab={setTab} label="All" count={items.length} />
          {Object.entries(KIND_META).map(([k, meta]) => (
            <TabBtn key={k} v={k} tab={tab} setTab={setTab} label={meta.label} count={items.filter((p) => p.kind === k).length} />
          ))}
        </div>
        <div className="relative ml-auto">
          <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search title / number / holder" className="w-64 h-9 pl-8 text-sm" data-testid="policy-search" />
        </div>
      </div>

      {/* List */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {filtered.length === 0 && (
          <div className="col-span-full text-slate-500 text-sm border border-dashed border-slate-200 p-8 text-center rounded-md">
            No records yet. {canEdit && "Click New policy to add one."}
          </div>
        )}
        {filtered.map((p) => (
          <PolicyCard key={p.id} p={p} canEdit={canEdit}
            onOpen={() => setDetail(p)} onEdit={() => openEdit(p)}
            onDel={() => del(p)} onRenew={() => setRenewDlg(p)}
            onAddPremium={() => setPremDlg(p)}
          />
        ))}
      </div>

      {/* New/edit dialog */}
      <PolicyDialog open={dlg} onOpenChange={setDlg} form={form} setForm={setForm}
        types={types} partners={partners} investments={investments}
        editing={editing}
        onAttachmentsChanged={(list) => {
          setEditing((e) => e ? { ...e, attachments: list } : e);
          load();
        }}
        onSave={savePolicy} />

      {/* Detail drawer */}
      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="max-w-2xl">
          {detail && (
            <DetailView p={items.find((x) => x.id === detail.id) || detail}
              canEdit={canEdit}
              onDeletePremium={async (premId) => {
                await api.delete(`/policies/${detail.id}/premiums/${premId}`);
                await load();
              }}
              typeName={(types.find((t) => t.id === detail.type_id) || {}).name}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Renew dialog */}
      <RenewDialog open={!!renewDlg} policy={renewDlg} onClose={() => setRenewDlg(null)} onDone={() => { setRenewDlg(null); load(); }} />

      {/* Premium payment dialog */}
      <PremiumDialog open={!!premDlg} policy={premDlg} onClose={() => setPremDlg(null)} onDone={() => { setPremDlg(null); load(); }} />

      {/* Add-type dialog */}
      <Dialog open={typeDlg} onOpenChange={setTypeDlg}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>New policy type</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input value={typeForm.name} onChange={(e) => setTypeForm({ ...typeForm, name: e.target.value })} placeholder="e.g. GST Registration" className="mt-1.5" data-testid="type-name" /></div>
            <div>
              <Label>Kind</Label>
              <Select value={typeForm.kind} onValueChange={(v) => setTypeForm({ ...typeForm, kind: v })}>
                <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(KIND_META).map(([k, m]) => <SelectItem key={k} value={k}>{m.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTypeDlg(false)}>Cancel</Button>
            <Button onClick={saveType} className="bg-red-600 hover:bg-red-700 text-white" data-testid="save-type">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ─── Sub components ────────────────────────────────────────── */

function Metric({ label, value, icon: Icon, tone, testId }) {
  return (
    <div className="p-4 border border-slate-200 bg-white">
      <div className="flex items-center gap-2 text-[10px] tracking-wider uppercase text-slate-500">
        <Icon className="h-3 w-3" /> {label}
      </div>
      <div className={`mt-1.5 font-mono-fin text-xl font-semibold ${tone || "text-slate-900"}`} data-testid={testId}>{value}</div>
    </div>
  );
}

function TabBtn({ v, tab, setTab, label, count }) {
  const active = tab === v;
  return (
    <button onClick={() => setTab(v)}
      data-testid={`tab-${v}`}
      className={`px-3 py-1.5 rounded text-[12px] font-medium inline-flex items-center gap-1.5 transition-colors ${
        active ? "bg-slate-900 text-white" : "text-slate-600 hover:text-slate-900"
      }`}>
      {label} <span className={`text-[10px] font-mono-fin ${active ? "text-white/70" : "text-slate-400"}`}>{count}</span>
    </button>
  );
}

function PolicyCard({ p, canEdit, onOpen, onEdit, onDel, onRenew, onAddPremium }) {
  const meta = KIND_META[p.kind] || KIND_META.other;
  const Icon = meta.icon;
  const light = LIGHT[p.expiry_light] || LIGHT.grey;
  const isRenewed = p.status === "renewed";
  const isLapsed = p.status === "lapsed";
  return (
    <div className={`border border-slate-200 bg-white rounded-md p-4 hover:shadow-sm transition-shadow ${isRenewed ? "opacity-60" : ""}`} data-testid={`policy-card-${p.id}`}>
      <div className="flex items-start justify-between gap-3">
        <button onClick={onOpen} className="text-left flex items-start gap-2.5 flex-1 min-w-0">
          <div className={`p-1.5 border rounded ${meta.accent} flex-shrink-0`}>
            <Icon className="h-3.5 w-3.5" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[10px] tracking-wider uppercase text-slate-500 truncate">{p.type_name || meta.label}</div>
            <div className="text-sm font-medium text-slate-900 leading-tight mt-0.5 truncate">{p.title}</div>
            {p.number && <div className="text-[11px] text-slate-500 font-mono-fin mt-0.5 truncate">{p.number}</div>}
          </div>
        </button>
        <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 border ${light.cls} rounded-sm flex-shrink-0`} data-testid={`light-${p.id}`}>
          {isRenewed ? "RENEWED" : isLapsed ? "LAPSED" : light.label}
        </span>
      </div>

      <div className="mt-3 grid grid-cols-2 gap-2 text-[11px]">
        <div>
          <div className="text-slate-500 uppercase tracking-wider text-[9px]">Expiry</div>
          <div className="font-mono-fin text-slate-800 mt-0.5">{p.expiry_date ? formatDate(p.expiry_date) : "-"}</div>
        </div>
        <div>
          <div className="text-slate-500 uppercase tracking-wider text-[9px]">Days left</div>
          <div className={`font-mono-fin mt-0.5 ${p.days_to_expiry !== null && p.days_to_expiry <= 15 ? "text-red-600" : "text-slate-800"}`}>
            {p.days_to_expiry !== null && p.days_to_expiry !== undefined ? (p.days_to_expiry < 0 ? `${Math.abs(p.days_to_expiry)}d over` : `${p.days_to_expiry}d`) : "-"}
          </div>
        </div>
        <div>
          <div className="text-slate-500 uppercase tracking-wider text-[9px]">Amount paid</div>
          <div className="font-mono-fin text-slate-800 mt-0.5">{formatRupees(p.amount_paid || 0)}</div>
        </div>
        <div>
          <div className="text-slate-500 uppercase tracking-wider text-[9px]">Next premium</div>
          <div className="font-mono-fin text-slate-800 mt-0.5">{p.next_premium_date ? formatDate(p.next_premium_date) : "-"}</div>
        </div>
      </div>

      {(p.holder_name || p.issued_by) && (
        <div className="mt-2.5 pt-2.5 border-t border-slate-100 text-[11px] text-slate-500 truncate">
          {p.issued_by ? `${p.issued_by}` : ""}{p.holder_name ? ` · ${p.holder_name}` : ""}
        </div>
      )}

      {canEdit && !isRenewed && (
        <div className="mt-3 flex items-center gap-1 pt-2 border-t border-slate-100">
          <button onClick={onAddPremium} className="text-[11px] px-2 py-1 border border-slate-200 hover:bg-slate-50 rounded-sm inline-flex items-center gap-1" data-testid={`prem-${p.id}`}>
            <BadgeIndianRupee className="h-3 w-3" /> Premium
          </button>
          <button onClick={onRenew} className="text-[11px] px-2 py-1 border border-slate-200 hover:bg-slate-50 rounded-sm inline-flex items-center gap-1" data-testid={`renew-${p.id}`}>
            <RefreshCw className="h-3 w-3" /> Renew
          </button>
          {p.payment_link && (
            <a href={p.payment_link} target="_blank" rel="noreferrer" className="text-[11px] px-2 py-1 border border-red-200 bg-red-50 text-red-700 hover:bg-red-100 rounded-sm inline-flex items-center gap-1" data-testid={`pay-${p.id}`}>
              <ExternalLink className="h-3 w-3" /> Pay now
            </a>
          )}
          <button onClick={onEdit} className="ml-auto p-1 text-slate-500 hover:text-slate-900" data-testid={`edit-${p.id}`}><Pencil className="h-3.5 w-3.5" /></button>
          <button onClick={onDel} className="p-1 text-slate-400 hover:text-red-600" data-testid={`del-${p.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
        </div>
      )}
    </div>
  );
}

function PolicyDialog({ open, onOpenChange, form, setForm, types, partners, investments, editing, onSave, onAttachmentsChanged }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{editing ? "Edit policy" : "New policy / license / insurance"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2">
              <Label>Title <span className="text-red-600">*</span></Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Cafe Fire Insurance FY26" className="mt-1.5" data-testid="pol-title" />
            </div>
            <div>
              <Label>Type</Label>
              <Select value={form.type_id || "none"} onValueChange={(v) => setForm({ ...form, type_id: v === "none" ? "" : v })}>
                <SelectTrigger className="mt-1.5" data-testid="pol-type"><SelectValue placeholder="— Choose type —" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— None —</SelectItem>
                  {types.map((t) => <SelectItem key={t.id} value={t.id}>{t.name} <span className="text-slate-400">· {t.kind}</span></SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Policy / License number</Label>
              <Input value={form.number} onChange={(e) => setForm({ ...form, number: e.target.value })} className="mt-1.5 font-mono-fin" data-testid="pol-number" />
            </div>
            <div>
              <Label>Issued by</Label>
              <Input value={form.issued_by} onChange={(e) => setForm({ ...form, issued_by: e.target.value })} placeholder="Insurer / Authority" className="mt-1.5" />
            </div>
            <div>
              <Label>Holder name</Label>
              <Input value={form.holder_name} onChange={(e) => setForm({ ...form, holder_name: e.target.value })} placeholder="MARKSAM Ventures LLP or partner name" className="mt-1.5" data-testid="pol-holder" />
            </div>
            <div>
              <Label>Linked partner (optional)</Label>
              <Select value={form.partner_id || "none"} onValueChange={(v) => setForm({ ...form, partner_id: v === "none" ? "" : v })}>
                <SelectTrigger className="mt-1.5"><SelectValue placeholder="— None —" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— None —</SelectItem>
                  {partners.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Linked project (optional)</Label>
              <Select value={form.investment_id || "none"} onValueChange={(v) => setForm({ ...form, investment_id: v === "none" ? "" : v })}>
                <SelectTrigger className="mt-1.5"><SelectValue placeholder="— None —" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— None —</SelectItem>
                  {investments.map((i) => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="border-t border-slate-100 pt-4">
            <div className="text-[10px] tracking-wider uppercase text-slate-500 mb-2">Dates</div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div><Label>Start date <span className="text-red-600">*</span></Label><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="mt-1.5" data-testid="pol-start" /></div>
              <div><Label>Expiry / renewal date <span className="text-red-600">*</span></Label><Input type="date" value={form.expiry_date} onChange={(e) => setForm({ ...form, expiry_date: e.target.value })} className="mt-1.5" data-testid="pol-expiry" /></div>
              <div><Label>Maturity date (insurance)</Label><Input type="date" value={form.maturity_date} onChange={(e) => setForm({ ...form, maturity_date: e.target.value })} className="mt-1.5" /></div>
              <div><Label>Next premium due</Label><Input type="date" value={form.next_premium_date} onChange={(e) => setForm({ ...form, next_premium_date: e.target.value })} className="mt-1.5" data-testid="pol-nextprem" /></div>
            </div>
          </div>

          <div className="border-t border-slate-100 pt-4">
            <div className="text-[10px] tracking-wider uppercase text-slate-500 mb-2">Money</div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div><Label>Initial amount paid (Rs)</Label><Input type="number" value={form.amount_paid} onChange={(e) => setForm({ ...form, amount_paid: e.target.value })} className="mt-1.5 font-mono-fin text-right" data-testid="pol-amount" /></div>
              <div><Label>Payment date</Label><Input type="date" value={form.payment_date} onChange={(e) => setForm({ ...form, payment_date: e.target.value })} className="mt-1.5" /></div>
              <div><Label>Sum assured (insurance)</Label><Input type="number" value={form.sum_assured} onChange={(e) => setForm({ ...form, sum_assured: e.target.value })} className="mt-1.5 font-mono-fin text-right" /></div>
              <div>
                <Label>Premium frequency</Label>
                <Select value={form.premium_frequency} onValueChange={(v) => setForm({ ...form, premium_frequency: v })}>
                  <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>{FREQ.map((f) => <SelectItem key={f.v} value={f.v}>{f.l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <div className="border-t border-slate-100 pt-4">
            <div><Label>Document URL <span className="text-slate-400 text-[10px]">(optional external link)</span></Label><Input value={form.document_url} onChange={(e) => setForm({ ...form, document_url: e.target.value })} placeholder="https://…" className="mt-1.5" /></div>
            <div className="mt-3">
              <Label>Payment link <span className="text-slate-400 text-[10px]">(Razorpay / PayU / bank URL — used for &ldquo;Pay now&rdquo; on advices)</span></Label>
              <Input value={form.payment_link} onChange={(e) => setForm({ ...form, payment_link: e.target.value })} placeholder="https://razorpay.me/@…" className="mt-1.5" data-testid="pol-paylink" />
            </div>
            <div className="mt-3"><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1.5" /></div>

            <div className="mt-4">
              <AttachmentsField
                parentId={editing?.id}
                attachments={editing?.attachments || []}
                onChange={onAttachmentsChanged}
                uploadUrl={(pid) => `/policies/${pid}/attachments`}
                deleteUrl={(pid, aid) => `/policies/${pid}/attachments/${aid}`}
                label="Files (policy doc, receipt, endorsements)"
              />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={onSave} className="bg-red-600 hover:bg-red-700 text-white" data-testid="pol-save">{editing ? "Save changes" : "Add policy"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DetailView({ p, canEdit, onDeletePremium, typeName }) {
  const meta = KIND_META[p.kind] || KIND_META.other;
  const totalPaid = (p.premiums || []).reduce((a, x) => a + Number(x.amount || 0), 0);
  return (
    <>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <span className={`px-2 py-0.5 text-[10px] uppercase tracking-wider border rounded ${meta.accent}`}>{p.type_name || meta.label}</span>
          {p.title}
        </DialogTitle>
      </DialogHeader>
      <div className="space-y-4 text-sm">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-[12px]">
          <Field label="Number" value={p.number || "-"} mono />
          <Field label="Issued by" value={p.issued_by || "-"} />
          <Field label="Holder" value={p.holder_name || "-"} />
          <Field label="Start" value={p.start_date ? formatDate(p.start_date) : "-"} />
          <Field label="Expiry" value={p.expiry_date ? formatDate(p.expiry_date) : "-"} />
          <Field label="Maturity" value={p.maturity_date ? formatDate(p.maturity_date) : "-"} />
          <Field label="Sum assured" value={formatRupees(p.sum_assured || 0)} mono />
          <Field label="Frequency" value={(FREQ.find((f) => f.v === p.premium_frequency) || {}).l || "-"} />
          <Field label="Next premium" value={p.next_premium_date ? formatDate(p.next_premium_date) : "-"} />
        </div>
        {p.document_url && (
          <a href={p.document_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-red-600 hover:text-red-700 text-[12px]">
            <ExternalLink className="h-3.5 w-3.5" /> Open document
          </a>
        )}
        {(p.attachments || []).length > 0 && (
          <div className="border-t border-slate-100 pt-3">
            <AttachmentsField
              parentId={p.id}
              attachments={p.attachments}
              readOnly
              uploadUrl={() => ""}
              deleteUrl={() => ""}
              label="Attached files"
            />
          </div>
        )}
        {p.notes && <div className="border-t border-slate-100 pt-3 text-[12px] text-slate-600 whitespace-pre-wrap">{p.notes}</div>}

        <div className="border-t border-slate-100 pt-4">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] tracking-wider uppercase text-slate-500">Premium history</div>
            <div className="text-[11px] text-slate-500">
              Total paid: <span className="font-mono-fin text-slate-900">{formatRupees(totalPaid)}</span>
            </div>
          </div>
          {(!p.premiums || p.premiums.length === 0) ? (
            <div className="text-[12px] text-slate-500 border border-dashed border-slate-200 p-4 text-center rounded-md">No premiums recorded yet.</div>
          ) : (
            <table className="w-full text-[12px]" data-testid="premium-history">
              <thead className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-wider text-slate-500">
                <tr><th className="p-2 text-left">Date</th><th className="p-2 text-right">Amount</th><th className="p-2 text-left">Reference</th><th className="p-2 text-left">Note</th>{canEdit && <th />}</tr>
              </thead>
              <tbody>
                {(p.premiums || []).map((pr) => (
                  <tr key={pr.id} className="border-b border-slate-100">
                    <td className="p-2">{formatDate(pr.date)}</td>
                    <td className="p-2 text-right font-mono-fin">{formatRupees(pr.amount)}</td>
                    <td className="p-2 text-slate-600 font-mono-fin">{pr.reference || "-"}</td>
                    <td className="p-2 text-slate-600">{pr.note || "-"}</td>
                    {canEdit && (
                      <td className="p-2 text-right">
                        <button onClick={() => onDeletePremium(pr.id)} className="text-slate-400 hover:text-red-600" data-testid={`del-prem-${pr.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </>
  );
}

function Field({ label, value, mono }) {
  return (
    <div>
      <div className="text-[9px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`mt-0.5 text-slate-900 ${mono ? "font-mono-fin" : ""}`}>{value}</div>
    </div>
  );
}

function RenewDialog({ open, policy, onClose, onDone }) {
  const [form, setForm] = useState({ start_date: "", expiry_date: "", amount_paid: "", payment_date: todayISO(), next_premium_date: "", number: "" });
  useEffect(() => {
    if (!policy) return;
    // Default: next year from previous expiry
    const nextStart = policy.expiry_date || todayISO();
    let nextExp = "";
    if (nextStart) {
      const d = new Date(nextStart);
      d.setFullYear(d.getFullYear() + 1);
      nextExp = d.toISOString().slice(0, 10);
    }
    setForm({ start_date: nextStart, expiry_date: nextExp, amount_paid: policy.amount_paid || "", payment_date: todayISO(), next_premium_date: "", number: policy.number || "" });
  }, [policy]);

  const submit = async () => {
    if (!form.start_date || !form.expiry_date) return toast.error("Start & expiry dates required");
    try {
      await api.post(`/policies/${policy.id}/renew`, {
        ...form, amount_paid: Number(form.amount_paid || 0),
        payment_date: form.payment_date || null,
        next_premium_date: form.next_premium_date || null,
      });
      toast.success("Policy renewed");
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Renew {policy?.title}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Renewed number</Label><Input value={form.number} onChange={(e) => setForm({ ...form, number: e.target.value })} className="mt-1.5 font-mono-fin" data-testid="renew-number" /></div>
          <div><Label>Amount paid (Rs)</Label><Input type="number" value={form.amount_paid} onChange={(e) => setForm({ ...form, amount_paid: e.target.value })} className="mt-1.5 font-mono-fin text-right" data-testid="renew-amount" /></div>
          <div><Label>New start date</Label><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="mt-1.5" data-testid="renew-start" /></div>
          <div><Label>New expiry date</Label><Input type="date" value={form.expiry_date} onChange={(e) => setForm({ ...form, expiry_date: e.target.value })} className="mt-1.5" data-testid="renew-expiry" /></div>
          <div><Label>Payment date</Label><Input type="date" value={form.payment_date} onChange={(e) => setForm({ ...form, payment_date: e.target.value })} className="mt-1.5" /></div>
          <div><Label>Next premium due</Label><Input type="date" value={form.next_premium_date} onChange={(e) => setForm({ ...form, next_premium_date: e.target.value })} className="mt-1.5" /></div>
        </div>
        <p className="text-[11px] text-slate-500 mt-2">
          A new record will be created and this policy will be marked as <span className="font-mono-fin">renewed</span>. History is preserved.
        </p>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} className="bg-red-600 hover:bg-red-700 text-white" data-testid="renew-save"><RefreshCw className="h-4 w-4 mr-1.5" /> Renew</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PremiumDialog({ open, policy, onClose, onDone }) {
  const [form, setForm] = useState({ date: todayISO(), amount: "", reference: "", note: "" });
  useEffect(() => { if (policy) setForm({ date: todayISO(), amount: policy.amount_paid || "", reference: "", note: "" }); }, [policy]);
  const submit = async () => {
    if (!form.amount) return toast.error("Amount required");
    try {
      await api.post(`/policies/${policy.id}/premiums`, { ...form, amount: Number(form.amount) });
      toast.success("Premium recorded");
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Record premium for {policy?.title}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Date</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1.5" data-testid="prem-date" /></div>
          <div><Label>Amount (Rs)</Label><Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="mt-1.5 font-mono-fin text-right" data-testid="prem-amount" /></div>
          <div><Label>Reference (cheque / UTR)</Label><Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} className="mt-1.5 font-mono-fin" /></div>
          <div><Label>Note</Label><Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="mt-1.5" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} className="bg-red-600 hover:bg-red-700 text-white" data-testid="prem-save">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
